// Componente FormularioCargaHora
import React, { useState, useEffect, useRef, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getClientesUsuario, limpiarServicios,getServicios, getTareas, cargarHoras, actualizarHoras, actualizarTodosEstadosTarjetas, getTareasDefault } from '../../actions/index.js';
import './FormularioCargaHora.css';
import Select from 'react-select';
import swal from 'sweetalert';
import { logger } from '../../utils/logger.js';
import { startOfWeek, addDays, format } from 'date-fns';
import es from 'date-fns/locale/es';

const FormularioCargaHora = ({ fecha, onSubmit, onClose, horas, isEditing, onFormSubmit, totalHorasDiaPrevio = 0, duplicarDe = null, modoSemanal = false }) => {
  // Modo "multi-día": se muestran los chips de días y se carga en varios días a la vez
  const multiDia = !!duplicarDe || modoSemanal;
  // Tope máximo de horas por día
  const TOPE_HORAS_DIA = 12;
  const dispatch = useDispatch();
  const clientes = useSelector(state => state.clientes);
  const todasLasHoras = useSelector(state => Array.isArray(state.horas) ? state.horas : []);
  const [diasDestino, setDiasDestino] = useState([]);
  const servicios = useSelector(state => state.servicios);
  const tareas = useSelector(state => state.tareasToservicio);
  const usuario = useSelector(state => state.session);
  const serviciosCargando = useSelector(state => state.serviciosCargando);
  const tareasCargando = useSelector(state => state.tareasCargando);
  const [clienteId, setClienteId] = useState('');
  const [servicioId, setServicioId] = useState('');
  const [tareaId, setTareaId] = useState('');
  const [horasTrabajadas, setHorasTrabajadas] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fechaCarga, setFechaCarga] = useState(fecha);
  const isMounted = useRef(true);
  const tareasDefault = useSelector(state => state.tareasDefault);
  const [tareasMostradas, setTareasMostradas] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [enviandoFormulario, setEnviandoFormulario] = useState(false);
  
// Este useEffect se ejecuta una vez cuando el componente se monta. Se encarga de obtener los clientes del usuario, las tareas por defecto y actualizar todos los estados de las tarjetas.
  useEffect(() => {
    dispatch(getClientesUsuario());
    dispatch(getTareasDefault());
    dispatch(actualizarTodosEstadosTarjetas());
  }, []); //

// Este useEffect se ejecuta cada vez que cambia servicioId, tareas o tareasDefault. Se encarga de establecer las tareas mostradas en función del servicio seleccionado.
  useEffect(() => {
    if (servicioId === '') {
      setTareasMostradas(tareasDefault);
    } else {
      setTareasMostradas(tareas);
    }
  }, [servicioId, tareas, tareasDefault]);

// Este useEffect se ejecuta cada vez que cambian horas o clientes. Se encarga de establecer los valores de los campos del formulario en función de las horas seleccionadas. 
//es decir, para saber si se está editando o cargando un nuevo registro. 
  useEffect(() => {
    if (horas) {
      setClienteId(horas.clienteId || '');
      setServicioId(horas.servicioId || '');
      setTareaId(horas.tareaId || '');
      setHorasTrabajadas(horas.horas || '');
      setDescripcion(horas.descripcion || '');
      const cliente = clientes.find(cliente => cliente.id === horas.clienteId);
      if (cliente) {
        setInputValue(cliente.nombre); // Muestra el nombre del cliente del registro que se está actualizando
        dispatch(getServicios(cliente.id)); // Carga los servicios para el cliente seleccionado
        
        // Si hay un servicioId, cargar las tareas para ese servicio
        if (horas.servicioId) {
          dispatch(getTareas(horas.servicioId));
        }
      }
    }
  }, [horas, clientes]);

// Precarga los campos cuando se está DUPLICANDO una entrada (registro nuevo con los mismos datos).
  useEffect(() => {
    if (duplicarDe) {
      setDiasDestino([fecha]); // por defecto, el mismo día de la entrada original
      setClienteId(duplicarDe.clienteId || '');
      setServicioId(duplicarDe.servicioId || '');
      setTareaId(duplicarDe.tareaId || '');
      setHorasTrabajadas(duplicarDe.horas || '');
      setDescripcion(duplicarDe.descripcion || '');
      const cliente = clientes.find(c => c.id === duplicarDe.clienteId);
      if (cliente) {
        setInputValue(cliente.nombre);
        dispatch(getServicios(cliente.id));
        if (duplicarDe.servicioId) {
          dispatch(getTareas(duplicarDe.servicioId));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicarDe, clientes]);

// En modo "carga semanal" arranca con los días hábiles (Lun-Vie) preseleccionados.
  useEffect(() => {
    if (modoSemanal && fecha) {
      const [d, m, a] = fecha.split('/');
      const inicio = startOfWeek(new Date(a, m - 1, d), { weekStartsOn: 1 });
      const habiles = Array.from({ length: 5 }).map((_, i) => format(addDays(inicio, i), 'dd/MM/yyyy', { locale: es }));
      setDiasDestino(habiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoSemanal, fecha]);

// Este useEffect se ejecuta cada vez que cambia fecha. Se encarga de establecer la fecha de carga.
  useEffect(() => {
    setFechaCarga(fecha);
  }, [fecha]);

// Este useEffect se ejecuta cuando el componente se monta y se desmonta. Se encarga de limpiar los servicios cuando el componente se desmonta.
// Esto es necesario para evitar que los servicios de un cliente anterior se muestren cuando se carga un nuevo registro.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      dispatch(limpiarServicios()); // Limpia los servicios cuando el componente se desmonta
    };
  }, []);

// Esta función se encarga de manejar el envío del formulario.
// Realiza la validación de las horas trabajadas, el cliente y la tarea seleccionados.
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Validación de horas
    if (horasTrabajadas <= 0) {
      alert('Las horas trabajadas deben ser mayores a cero.');
      return;
    }

    // Validación del tope diario (TOPE_HORAS_DIA por día)
    if (multiDia) {
      // Duplicando en varios días: debe haber al menos un día y ninguno debe superar el tope
      if (diasDestino.length === 0) {
        swal({ title: "Elegí al menos un día", text: "Seleccioná en qué día(s) duplicar la carga.", icon: "error", className: "alertaEliminarr", buttons: { confirm: { text: "OK", className: "alertaBtn1" } } });
        return;
      }
      const diasExcedidos = diasDestino.filter(f => totalDelDia(f) + Number(horasTrabajadas) > TOPE_HORAS_DIA);
      if (diasExcedidos.length > 0) {
        swal({ title: "Tope de horas", text: `En ${diasExcedidos.length} de los días elegidos se superarían las ${TOPE_HORAS_DIA} h. Revisá esos días.`, icon: "error", className: "alertaEliminarr", buttons: { confirm: { text: "OK", className: "alertaBtn1" } } });
        return;
      }
    } else {
      const totalDiaConEsta = Number(totalHorasDiaPrevio || 0) + Number(horasTrabajadas);
      if (totalDiaConEsta > TOPE_HORAS_DIA) {
        const yaCargadas = Number(totalHorasDiaPrevio || 0);
        swal({
          title: "Tope de horas",
          text: `El total del día no puede superar las ${TOPE_HORAS_DIA} h.` +
            (yaCargadas > 0 ? ` Ya hay ${yaCargadas} h cargadas ese día.` : ''),
          icon: "error",
          className: "alertaEliminarr",
          buttons: { confirm: { text: "OK", className: "alertaBtn1" } }
        });
        return;
      }
    }

// Validación de cliente o tarea por defecto
     if (!clienteId && tareaId === '') {
  swal({
    title: "Oops...",
    text: "Debe seleccionar un cliente o una tarea por defecto.",
    icon: "error",
    className: "alertaEliminarr", // Agrega la clase CSS a la alerta
    buttons: {
      confirm: {
        text: "OK",
        className: "alertaBtn1" // Agrega la clase CSS al botón
      }
    }
  });
  return;
     } else if (clienteId && tareaId === '') {
      swal({
      title: "Oops...",
      text: "Debe seleccionar una tarea.",
      icon: "error",
     className: "alertaEliminarr", // Agrega la clase CSS a la alerta
      buttons: {
      confirm: {
        text: "OK",
         className: "alertaBtn1" // Agrega la clase CSS al botón
       }
      }
      });
         return;
       }
       // Si se están actualizando horas, llama a handleActualizarHoras. 
       //Si se están cargando nuevas horas, llama a handleCargarHoras.
    if (horas) {
      await handleActualizarHoras(e);
     } else {
      await handleCargarHoras(e);
      dispatch(limpiarServicios()); // Limpia los servicios después de cargar el nuevo registro
      setServicioId('');
     }
    // Llama a la función de callback para manejar la actualización del estado de carga en CardDia
   // onFormSubmit();
  };

 // Esta función se ejecuta cuando cambia el cliente seleccionado.
 // Se encarga de obtener los servicios del cliente seleccionado.
  const handleClienteChange = (clientId) => {
    setClienteId(clientId);
   
    if (clientId) {
      dispatch(getServicios(clientId));
     
    }
  };

  // Esta función se encarga de eliminar los duplicados de un array de objetos en función de una clave.
  // Se utiliza para eliminar los clientes duplicados en el select de clientes.
  const eliminarDuplicados = (arr, key) => {
    return arr.reduce((item, i) => {
      const existente = item.find(j => j[key] === i[key]);
      if (!existente) {
        item.push(i);
      }
      return item;
    }, []);
  };

  // Obtiene los clientes sin duplicados
  const clientesSinDuplicados = eliminarDuplicados(clientes, 'id');

  // Crea las opciones de clientes para el select
  const opcionesClientes = clientesSinDuplicados.map(cliente => ({ value: cliente.id, label: cliente.nombre }));

  // Crea las opciones de servicios para el select
  const opcionesServicios = Array.isArray(servicios) ? servicios.map(servicio => ({
    value: servicio.id,
    label: servicio.nombre
  })) : [];

  // Crea las opciones de tareas para el select
  const opcionesTareas = Array.isArray(tareasMostradas) ? tareasMostradas.map((tarea, index) => ({
    value: tarea?.id,
    label: tarea?.nombre
  })) : [];

  // Estilos comunes para todos los react-select (paleta moderna oscura + verde)
  const estilosSelect = {
    control: (provided, state) => ({
      ...provided,
      fontFamily: 'Roboto, sans-serif',
      backgroundColor: '#0A100D',
      color: '#E8EFEA',
      outline: 'none',
      border: state.isFocused ? '1px solid rgba(40,199,111,0.6)' : '0.5px solid rgba(255,255,255,0.10)',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(40,199,111,0.20)' : 'none',
      borderRadius: '9px',
      fontSize: '13px',
      minHeight: '36px',
      width: '100%',
      transition: '0.15s ease',
      ':hover': {
        borderColor: 'rgba(40,199,111,0.45)',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#0C120F',
      color: '#E8EFEA',
      borderRadius: '11px',
      overflow: 'hidden',
      marginTop: '6px',
      zIndex: 1000,
      border: '0.5px solid rgba(40,199,111,0.25)',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '6px',
      maxHeight: '230px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'rgba(40,199,111,0.16)'
        : (state.isFocused ? 'rgba(40,199,111,0.12)' : 'transparent'),
      color: state.isSelected || state.isFocused ? '#EAF6EF' : '#CBD6CF',
      fontSize: '14px',
      cursor: 'pointer',
      borderRadius: '8px',
      padding: '9px 11px',
      transition: 'background 0.15s ease',
      ':active': {
        backgroundColor: 'rgba(40,199,111,0.20)',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#E8EFEA',
    }),
    input: (provided) => ({
      ...provided,
      color: '#E8EFEA',
      margin: 0,
      padding: 0,
      caretColor: '#3FE07F',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 13px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9FB0A6',
      fontSize: '14px',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#3FE07F' : '#5C6862',
      ':hover': { color: '#3FE07F' },
    }),
  };

// Esta función se ejecuta cuando cambia el servicio seleccionado.
// Se encarga de obtener las tareas del servicio seleccionado.
  const handleServicioChange = (e) => {
    const value = e && e.target ? e.target.value : e; // Compatibilidad con react-select y select nativo
    setServicioId(value);
    if (value === '' || !value) {
      setTareaId(tareasDefault);
    } else {
      dispatch(getTareas(value)); // Usar 'value' en lugar de 'e.target.value'
    }
  };

// Esta función se ejecuta cuando cambia la tarea seleccionada.
  const handleTareaChange = (e) => {
    const value = e && e.target ? e.target.value : e; // Compatibilidad con react-select y select nativo
    setTareaId(value || '');
  };

 // Este useEffect se ejecuta cada vez que cambian los servicios.
 // Se encarga de limpiar el servicio seleccionado SOLO si no estamos editando.
 useEffect(() => {
  if (!isEditing) {
    setServicioId('');
  }
}, [servicios, isEditing]);

// Esta función se encarga de cargar las horas en la base de datos.
const handleCargarHoras = async (e) => {
  // Evita que se envíe el formulario más de una vez
  e.preventDefault();
  // Cambia el estado de enviandoFormulario a true
  if (isMounted.current) {
    setEnviandoFormulario(true);
  }
  // Si estamos DUPLICANDO, se crea la carga en cada día elegido; si no, solo en la fecha de la tarjeta.
  const fechasDestino = multiDia && diasDestino.length > 0 ? diasDestino : [fecha];
  const datosBase = { clienteId, servicioId, tareaId, horasTrabajadas, descripcion, usuarioId: usuario.id, senorityhora: usuario.perfil };
  // Intenta cargar las horas en la base de datos
  try {
    let ultimoDato = null;
    for (const f of fechasDestino) {
      const datosFormulario = { ...datosBase, fechaCarga: f, cardId: f };
      const response = await dispatch(cargarHoras(datosFormulario));
      if (response && response.data && response.data.id) {
        datosFormulario.id = response.data.id;
      }
      ultimoDato = datosFormulario;
    }

    dispatch(actualizarTodosEstadosTarjetas());// Actualiza todos los estados de las tarjetas
    dispatch(limpiarServicios()); // Limpia los servicios después de cargar el nuevo registro
    setServicioId('');// Limpia el servicio seleccionado
    if (ultimoDato) onSubmit(ultimoDato);// callback con el último registro creado
    onClose();// Cierra el formulario

  } catch (error) {
    logger.error('Error al cargar horas:', error);
  } finally {
    // Cambia el estado de enviandoFormulario a false
    // para permitir que se envíe el formulario nuevamente
    if (isMounted.current) {
      setEnviandoFormulario(false);
    }
  }
};

// Esta función se encarga de actualizar las horas en la base de datos.
const handleActualizarHoras = async (e) => {
  // Evita que se envíe el formulario más de una vez
    e.preventDefault();
  // Crea un objeto con los datos del formulario
    const datosFormulario = { clienteId, servicioId, tareaId, horasTrabajadas, descripcion, usuarioId: usuario.id, senorityhora: usuario.perfil, fechaCarga: fecha };
  // Si horas y horas.id existen, actualiza las horas
    if (horas && horas.id) {
      const idOriginal = horas.id;// Guarda el id original de las horas
      // Llama a la acción actualizarHoras con los datos del formulario y el id de las horas
      const response = await dispatch(actualizarHoras({ ...datosFormulario, id: horas.id }));
      // Si la respuesta y la respuesta.id existen, establece el id del registro en datosFormulario
      const id = response && response.id ? response.id : idOriginal;
      onSubmit({ ...datosFormulario, id, idOriginal });// Llama a la función de callback onSubmit con los datos del formulario
      setServicioId('');// Limpia el servicio seleccionado
      onClose(); // Cierra el formulario
    // Si horas o horas.id no existen, muestra un mensaje de error
    } else {
      logger.log('horas o horas.id es undefined');
    }
  };

// Días de la semana a la que pertenece la fecha (para el selector de "Duplicar en")
const diasSemana = (() => {
  if (!fecha) return [];
  const [d, m, a] = fecha.split('/');
  const base = new Date(a, m - 1, d);
  const inicio = startOfWeek(base, { weekStartsOn: 1 });
  return Array.from({ length: 7 }).map((_, i) => {
    const dia = addDays(inicio, i);
    return {
      fecha: format(dia, 'dd/MM/yyyy', { locale: es }),
      etiqueta: format(dia, 'EEE d', { locale: es }), // ej: "lun 8"
    };
  });
})();

// Total de horas ya cargadas en una fecha (del usuario logueado)
const totalDelDia = (f) => todasLasHoras
  .filter(h => h.usuarioId === usuario.id && h.fechaCarga === f)
  .reduce((t, h) => t + Number(h.horas || 0), 0);

// Marca/desmarca un día destino al duplicar
const toggleDiaDestino = (f) => {
  setDiasDestino(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
};

// Renderiza el formulario
return (
   //se agrega el formulario de carga de horas
<form className='formCarga' onSubmit={handleSubmit}>
      <div className='formCarga__header'>
        <span className='formCarga__title'>{horas ? 'Editar horas' : modoSemanal ? 'Carga semanal' : duplicarDe ? 'Duplicar horas' : 'Cargar horas'}</span>
        <button className='btnCloseCarga' type="button" onClick={onClose} aria-label="Cerrar">
          <img src={require('../../images/cruz-verde.svg').default} alt="Cerrar" />
        </button>
      </div>

    {/* Cliente */}
    <div className='campo'>
      <label className='campo__label'>Cliente</label>
      <Select
        className="react-select-container"
        classNamePrefix="react-select"
        value={opcionesClientes.find(opcion => opcion.value === clienteId)}
        onChange={opcion => handleClienteChange(opcion.value)}
        options={opcionesClientes}
        placeholder="Buscá un cliente"
        openMenuOnClick={true}
        isSearchable
        components={{
          IndicatorSeparator: () => null, // Elimina completamente el separador
        }}
        styles={estilosSelect}
      />
    </div>

    {/* Servicio */}
    <div className='campo'>
      <label className='campo__label'>Servicio</label>
      <Select
        className="react-select-container"
        classNamePrefix="react-select"
        value={opcionesServicios.find(opcion => opcion.value === servicioId)}
        onChange={opcion => handleServicioChange(opcion?.value || '')}
        options={opcionesServicios}
        placeholder={serviciosCargando ? "Cargando servicios..." : "Seleccioná un servicio"}
        openMenuOnClick={true}
        isSearchable
        isDisabled={serviciosCargando}
        components={{
          IndicatorSeparator: () => null,
        }}
        styles={estilosSelect}
      />
    </div>

    {/* Tarea */}
    <div className='campo'>
      <label className='campo__label'>Tarea</label>
      <Select
        className="react-select-container"
        classNamePrefix="react-select"
        value={opcionesTareas.find(opcion => opcion.value === tareaId)}
        onChange={opcion => handleTareaChange(opcion?.value || '')}
        options={opcionesTareas}
        placeholder={tareasCargando ? "Cargando tareas..." : "Seleccioná una tarea"}
        openMenuOnClick={true}
        isSearchable
        isDisabled={tareasCargando}
        components={{
          IndicatorSeparator: () => null,
        }}
        styles={estilosSelect}
      />
    </div>

    {/* Horas */}
    <div className='campo'>
      <label className='campo__label'>Horas</label>
      <input placeholder='Ej: 3' type="number" min="0.01" step="0.01" value={horasTrabajadas} onChange={e => setHorasTrabajadas(e.target.value)} required />
    </div>

    {/* Descripción */}
    <div className='campo'>
      <label className='campo__label'>Descripción</label>
      <input placeholder='Detalle de la tarea…' type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)} required />
    </div>

    {/* Selector de días (duplicar / carga semanal) */}
    {multiDia && (
      <div className='campo'>
        <label className='campo__label'>{modoSemanal ? 'Cargar en los días' : 'Duplicar en los días'}</label>
        <div className='diasDestino'>
          {diasSemana.map(d => (
            <button
              type="button"
              key={d.fecha}
              className={`diaChip${diasDestino.includes(d.fecha) ? ' diaChip--activo' : ''}`}
              onClick={() => toggleDiaDestino(d.fecha)}
            >
              {d.etiqueta}
            </button>
          ))}
        </div>
      </div>
    )}

    <div className='formCarga__acciones'>
      <button className='btnCancelar' type="button" onClick={onClose}>Cancelar</button>
      {!horas && <button className='bntCargar' type="submit" disabled={enviandoFormulario}>{multiDia ? `${modoSemanal ? 'Cargar' : 'Duplicar'}${diasDestino.length > 1 ? ` (${diasDestino.length} días)` : ''}` : 'Cargar horas'}</button>}
      {horas && <button className='bntAct' type="submit">Actualizar horas</button>}
    </div>
</form>
  );
};

export default memo(FormularioCargaHora);